import { HomeWorkoutSequence } from 'fitbeat.gym-core';
import React, { FunctionComponent } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import CommunityIcon from '../../../assets/images/home-workouts/community.svg';
import LeaveVideoCallIcon from '../../../assets/images/home-workouts/leave-video-call.svg';
import MicrophoneOffIcon from '../../../assets/images/home-workouts/microphone-off.svg';
import MicrophoneOnIcon from '../../../assets/images/home-workouts/microphone-on.svg';
import VideoOffIcon from '../../../assets/images/home-workouts/video-off.svg';
import VideoOnIcon from '../../../assets/images/home-workouts/video-on.svg';
import { defaultScale, fullScale } from '../../../utils/scaling';
import Theme from '../.././../theme';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import { DeviceSetupMusicPlayer } from './DeviceSetupMusicPlayer';
import { disabledOpacity, IconButton } from './IconButton';

interface IProps {
  openSetupScreen: (screen: DeviceSetupScreens) => void;
  isGroupWorkout: boolean;
  landscape: boolean;
  localAudioIsMuted: boolean;
  localVideoIsMuted: boolean;
  isGroupWorkoutCallConnected: boolean;
  isGroupWorkoutCallConnecting: boolean;
  homeWorkoutSession: HomeWorkoutSequence | null;
  waitingToStartWorkout: boolean;
  groupWorkoutTalkingEnabled: boolean;
  remainingSecondsInSequence: number;
  preloadingComplete: boolean;
  t: (key: string, options?: any) => string;
  isPastWarmdown: boolean;
  isStreamingWorkoutAndNotHost: boolean;
  isStreamingWorkout: boolean;

  toggleLocalAudio(): void;

  toggleLocalVideoMuted(): void;

  disconnectGroupWorkoutCall(): void;

  initiateGroupWorkoutCall(callId: string): void;
}

export const DeviceSetupFooter: FunctionComponent<IProps> = ({
                                                               openSetupScreen,
                                                               isGroupWorkout,
                                                               localVideoIsMuted,
                                                               localAudioIsMuted,
                                                               toggleLocalAudio,
                                                               toggleLocalVideoMuted,
                                                               landscape = false,
                                                               isGroupWorkoutCallConnected,
                                                               disconnectGroupWorkoutCall,
                                                               initiateGroupWorkoutCall,
                                                               isGroupWorkoutCallConnecting,
                                                               homeWorkoutSession,
                                                               waitingToStartWorkout,
                                                               groupWorkoutTalkingEnabled,
                                                               remainingSecondsInSequence,
                                                               preloadingComplete,
                                                               isPastWarmdown,
                                                               isStreamingWorkoutAndNotHost,
                                                               isStreamingWorkout,
                                                               t,
                                                             }) => {

    const iconSize = {
      height: defaultScale(20),
      width: defaultScale(20),
    };

    const callConnected = isGroupWorkoutCallConnected && !isGroupWorkoutCallConnecting;

    const videoIconButton = () => {
      if (localVideoIsMuted || !callConnected || isStreamingWorkoutAndNotHost) {
        const isVideoButtonDisabled = !callConnected || isStreamingWorkoutAndNotHost;
        return <IconButton
          icon={<VideoOffIcon color={Theme.WHITE} {...iconSize}
            opacity={isVideoButtonDisabled ? disabledOpacity : 1} />}
          label={t('startVideo')}
          onPress={toggleLocalVideoMuted}
          disabled={isVideoButtonDisabled}
        />;
      }
      return <IconButton icon={<VideoOnIcon color={Theme.WHITE} {...iconSize} />} label={t('stopVideo')}
                         onPress={toggleLocalVideoMuted} />;
    };

    const audioIconButton = () => {
      if (localAudioIsMuted || !callConnected || !groupWorkoutTalkingEnabled || isStreamingWorkoutAndNotHost) {
        const isAudioDisabled = isStreamingWorkoutAndNotHost || !groupWorkoutTalkingEnabled;
        if (isAudioDisabled) {
          return <IconButton
            icon={<MicrophoneOffIcon color={Theme.WHITE} {...iconSize} opacity={disabledOpacity} />}
            label={t('unmuteCall')}
            onPress={toggleLocalAudio}
            disabled
          />;
        }
        return <IconButton
          icon={<MicrophoneOffIcon color={Theme.WHITE} {...iconSize}
                                   opacity={callConnected ? 1 : disabledOpacity} />}
          label={t('unmuteCall')}
          onPress={toggleLocalAudio}
          disabled={!callConnected}
        />;
      }
      return <IconButton icon={<MicrophoneOnIcon color={Theme.WHITE} {...iconSize} />} label={t('muteCall')}
                         onPress={toggleLocalAudio} />;
    };

    const leaveVideoCallIconButton = () => {
      const groupCallPairedId = homeWorkoutSession ? homeWorkoutSession.groupCallPairedId : undefined;
      if (isGroupWorkoutCallConnected) {
        return <IconButton icon={<CommunityIcon color={Theme.WHITE} {...iconSize} />} label={t('leaveVideoCall')}
                           onPress={disconnectGroupWorkoutCall} loading={isGroupWorkoutCallConnecting} />;
      }
      if (!preloadingComplete) {
        return <IconButton icon={<LeaveVideoCallIcon color={Theme.WHITE} {...iconSize} opacity={disabledOpacity} />}
                           label={t('joinVideoCall')}
                           onPress={() => null}
                           loading={isGroupWorkoutCallConnecting} disabled />;
      }
      return <IconButton icon={<LeaveVideoCallIcon color={Theme.WHITE} {...iconSize} />} label={t('joinVideoCall')}
                         onPress={() => initiateGroupWorkoutCall(groupCallPairedId)}
                         loading={isGroupWorkoutCallConnecting} />;
    };
    const isSoloWorkout = !isGroupWorkout;
    const isNonStreamingGroupWorkoutDuringExercises =
      !isStreamingWorkout && !waitingToStartWorkout && preloadingComplete && !isPastWarmdown;
    const isSteamingGroupWorkout = isStreamingWorkout;
    const shouldShowMusicControls =
      isSoloWorkout || isSteamingGroupWorkout || isNonStreamingGroupWorkoutDuringExercises;
    return (
      <>
        {
          isGroupWorkout &&
          <View style={landscape ? styles.landscapeContainer : styles.container}>
            <View style={styles.groupWorkoutControls}>
              {leaveVideoCallIconButton()}
              {videoIconButton()}
              {audioIconButton()}
            </View>
            {
              shouldShowMusicControls &&
              <View>
                <DeviceSetupMusicPlayer openSetupScreen={openSetupScreen} landscape={landscape}
                                        isGroupWorkout={isGroupWorkout} />
              </View>
            }
          </View>
        }
        {
          !isGroupWorkout && shouldShowMusicControls &&
          <View style={landscape ? styles.landscapeContainer : styles.container}>
            <View>
              <DeviceSetupMusicPlayer openSetupScreen={openSetupScreen}
                                      isGroupWorkout={isGroupWorkout}
                                      musicTitleContainerStyle={{ marginBottom: fullScale(10) }} />
            </View>
          </View>
        }
      </>
    );
  }
;

const styles = StyleSheet.create({
  container: {
    marginHorizontal: defaultScale(10),
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY_DARK_MEDIUM,
  },
  landscapeContainer: {
    marginHorizontal: defaultScale(10),
  },
  groupWorkoutControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopColor: Theme.GREY_DARK_MEDIUM, borderTopWidth: 1,
    paddingTop: fullScale(5),
  },
  text: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
    textAlign: 'center',
    paddingBottom: fullScale(20),
  },
});
